import { ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  BanknotesIcon,
  BuildingLibraryIcon,
  CheckIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  GiftIcon,
  TruckIcon,
} from '@heroicons/react/24/solid';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

interface PaymentMethod {
  id: string;
  name: string;
  description?: string;
}

interface PaymentMethodSelectProps {
  value: string;
  onChange: (value: string) => void;
  paymentMethods: PaymentMethod[];
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onMethodSelected?: () => void;
  className?: string;
  placeholder?: string;
}

export interface PaymentMethodSelectRef {
  focus: () => void;
}

// Fonction pour obtenir l'icône appropriée selon le nom de la méthode
const getPaymentIcon = (methodName: string) => {
  const name = methodName.toLowerCase();

  if (name.includes('espèce') || name.includes('cash')) {
    return <BanknotesIcon className="h-5 w-5" />;
  }
  if (name.includes('carte') || name.includes('card')) {
    return <CreditCardIcon className="h-5 w-5" />;
  }
  if (name.includes('virement') || name.includes('transfer')) {
    return <BuildingLibraryIcon className="h-5 w-5" />;
  }
  if (name.includes('chèque') || name.includes('cheque') || name.includes('check')) {
    return <CheckIcon className="h-5 w-5" />;
  }
  if (name.includes('paypal')) {
    return <CurrencyDollarIcon className="h-5 w-5" />;
  }
  if (name.includes('bon') || name.includes('gift') || name.includes('voucher')) {
    return <GiftIcon className="h-5 w-5" />;
  }
  if (name.includes('livraison') || name.includes('delivery')) {
    return <TruckIcon className="h-5 w-5" />;
  }

  // Icône par défaut
  return <CurrencyDollarIcon className="h-5 w-5" />;
};

export const PaymentMethodSelect = forwardRef<PaymentMethodSelectRef, PaymentMethodSelectProps>(
  (
    {
      value,
      onChange,
      paymentMethods,
      onKeyDown,
      onMethodSelected,
      className = '',
      placeholder = 'Sélectionner une méthode',
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const listItemsRef = useRef<(HTMLButtonElement | null)[]>([]);

    const selectedMethod = paymentMethods.find(m => m.name === value);

    // Exposer la méthode focus au parent
    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          buttonRef.current?.focus();
        },
      }),
      []
    );

    // Fermer le dropdown quand on clique ailleurs
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Gestion du clavier
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (isOpen && selectedIndex >= 0) {
          // Si le dropdown est ouvert et qu'un élément est survolé, le sélectionner
          handleSelect(paymentMethods[selectedIndex].name);
        } else if (!isOpen && value) {
          // Si le dropdown est fermé et qu'une valeur est déjà sélectionnée, passer au champ suivant
          if (onKeyDown) {
            onKeyDown(e);
          }
        } else if (!isOpen) {
          // Si le dropdown est fermé et qu'aucune valeur n'est sélectionnée, ouvrir le dropdown
          setIsOpen(true);
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setSelectedIndex(0);
        } else {
          const newIndex =
            selectedIndex < paymentMethods.length - 1 ? selectedIndex + 1 : selectedIndex;
          setSelectedIndex(newIndex);
          // Scroll vers l'élément sélectionné
          listItemsRef.current[newIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (isOpen) {
          const newIndex = selectedIndex > 0 ? selectedIndex - 1 : 0;
          setSelectedIndex(newIndex);
          // Scroll vers l'élément sélectionné
          listItemsRef.current[newIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      } else if (e.key === 'Escape') {
        setIsOpen(false);
        setSelectedIndex(-1);
      } else if (e.key === 'Tab') {
        setIsOpen(false);
        if (onKeyDown) {
          onKeyDown(e);
        }
      }
    };

    const handleSelect = (methodName: string) => {
      onChange(methodName);
      setIsOpen(false);
      setSelectedIndex(-1);
      if (onMethodSelected) {
        onMethodSelected();
      } else {
        buttonRef.current?.focus();
      }
    };

    return (
      <div ref={containerRef} className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          className={`${className} flex items-center justify-between w-full focus:outline-none`}
        >
          <div className="flex items-center gap-2">
            {selectedMethod ? (
              <>
                <span className="text-current">{getPaymentIcon(selectedMethod.name)}</span>
                <span>{selectedMethod.name}</span>
              </>
            ) : (
              <span className="text-gray-400">{placeholder}</span>
            )}
          </div>
          <ChevronDownIcon
            className={`h-5 w-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-token-md shadow-lg overflow-hidden max-h-60 overflow-y-auto">
            {paymentMethods.map((method, index) => (
              <button
                key={method.id}
                ref={el => {
                  listItemsRef.current[index] = el;
                }}
                type="button"
                onClick={() => handleSelect(method.name)}
                className={`w-full px-4 py-2 flex items-center gap-3 hover:bg-blue-50 transition-colors duration-150 ${
                  index === selectedIndex ? 'bg-blue-100' : ''
                } ${value === method.name ? 'bg-blue-50 font-medium' : ''}`}
              >
                <span className="text-blue-600">{getPaymentIcon(method.name)}</span>
                <span className="text-sm font-medium text-gray-900">{method.name}</span>
                {value === method.name && <CheckIcon className="h-4 w-4 text-blue-600 ml-auto" />}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
);

PaymentMethodSelect.displayName = 'PaymentMethodSelect';
